import { HireActiveStatus } from '@/gql/graphql'
import { btoaSafe } from '@/components/GraphqlDataTable/filters/filter-tools'
import { hireStatusOption } from '@/components/GraphqlDataTable/filters/hires/hireStatusOptions'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

type NamedAccount = { id: string; name: string }
type DateRange = { from: string; to: string }

const encode = (value: unknown) => btoaSafe(JSON.stringify(value))

/** The placements page's filter state is URL-encoded, so a preset view is just the right query string. */
function placementsUrl(client: NamedAccount, filters: Record<string, unknown> = {}): string {
  return laravelNamedRoute(namedRoutes.placements.index, {
    recruiterCompanies: encode([{ label: client.name, value: client.id }]),
    ...Object.fromEntries(Object.entries(filters).map(([key, value]) => [key, encode(value)])),
  })
}

export type PlacementScope = { account: NamedAccount; endClient?: NamedAccount }

/** An agency link's placements run through its managing company and are told apart by the end client. */
export const placementScope = (relation: {
  client: NamedAccount
  managingCompany?: NamedAccount | null
}): PlacementScope =>
  relation.managingCompany
    ? { account: relation.managingCompany, endClient: relation.client }
    : { account: relation.client }

const endClientFilter = (endClient?: NamedAccount) =>
  endClient ? { endClients: [{ label: endClient.name, value: endClient.id }] } : {}

/** Every placement at the client, whatever its status, unless a status list narrows it. */
export function clientPlacementsUrl(
  client: NamedAccount,
  statuses?: HireActiveStatus[],
  endClient?: NamedAccount,
): string {
  return placementsUrl(client, {
    ...endClientFilter(endClient),
    ...(statuses ? { activeStatus: statuses.map(hireStatusOption) } : {}),
  })
}

export function endingSoonPlacementsUrl(
  client: NamedAccount,
  endDateRange: DateRange,
  endClient?: NamedAccount,
): string {
  return placementsUrl(client, { ...endClientFilter(endClient), endDateRange })
}

export function startingSoonPlacementsUrl(
  client: NamedAccount,
  startDateRange: DateRange,
  endClient?: NamedAccount,
): string {
  return placementsUrl(client, { ...endClientFilter(endClient), startDateRange })
}

/** Placements where both the client and the staffing agency are involved. */
export function sharedPlacementsUrl(client: NamedAccount, agency: NamedAccount): string {
  return placementsUrl(client, { staffingAgencies: [{ label: agency.name, value: agency.id }] })
}

/** An agency's placements at a client, pinned to the company the link runs through, like its count. */
export function agencyClientPlacementsUrl(
  link: { client: NamedAccount; managingCompany: NamedAccount },
  statuses?: HireActiveStatus[],
): string {
  return clientPlacementsUrl(link.managingCompany, statuses, link.client)
}
